import React, { useEffect, useRef } from 'react'
import { clearLocalStorage } from '../services/auth.service'
import { storeUser, userExpiring, userExpired } from '../actions/auth.actions'
import authLogger from '../utils/authLogger'

const AuthenticationProvider = ({ userService: manager, store, children }) => {
  const userManagerRef = useRef(null)
  const hasLoadedUser = useRef(false)
  const silentSigninPromise = useRef(null)

  if (!userManagerRef.current) {
    userManagerRef.current = manager
  }
  const userManager = userManagerRef.current

  useEffect(() => {
    const EXPIRY_BUFFER_SECONDS = 60

    authLogger.info('AuthProvider', 'Initializing authentication provider')

    const onUserLoaded = user => {
      hasLoadedUser.current = true

      authLogger.info('AuthProvider', 'User loaded event', {
        expires_in: user?.expires_in,
        expires_at: user?.expires_at,
        expired: user?.expired,
      })
      authLogger.logUserState('AuthProvider', user)

      store.dispatch(storeUser(user))
    }

    const onUserUnloaded = () => {
      hasLoadedUser.current = false
      authLogger.warn('AuthProvider', 'User unloaded event - clearing local storage')
      clearLocalStorage()
    }

    const onAccessTokenExpiring = () => {
      authLogger.warn('AuthProvider', 'Token expiring event - attempting silent renewal')
      store.dispatch(userExpiring())
      userManager.signinSilent()
        .then(user => {
          if (user) {
            authLogger.info('AuthProvider', 'Silent renewal succeeded', {
              expires_in: user?.expires_in,
              expires_at: user?.expires_at,
            })
            store.dispatch(storeUser(user))
          } else {
            authLogger.warn('AuthProvider', 'Silent renewal returned no user')
          }
        })
        .catch(err => {
          authLogger.error('AuthProvider', 'Silent renewal failed in expiring handler', {
            error: err.message,
          })
          // Don't dispatch userExpired here - let onAccessTokenExpired handle it
          // This avoids double-handling if the token actually expires
        })
    }

    const onAccessTokenExpired = () => {
      authLogger.error('AuthProvider', 'Token expired event - dispatching userExpired action')
      store.dispatch(userExpired())
      // Don't automatically signout - let ProtectedRoute handle the redirect to login
      // Calling signoutRedirect() here causes users to go to logout page instead of login
    }

    const onSilentRenewError = err => {
      authLogger.error('AuthProvider', 'Silent renew error event', {
        error: err?.message || err,
      })
      // Note: onAccessTokenExpired will fire after this if token fully expires
      // ProtectedRoute will handle the redirect to login
    }

    const onUserSignedOut = async () => {
      if (!hasLoadedUser.current) {
        authLogger.info('AuthProvider', 'Ignoring early userSignedOut event')
        return
      }
      authLogger.warn('AuthProvider', 'User signed out event - redirecting to login')
      clearLocalStorage()
      await userManager.removeUser()
      userManager.signinRedirect()
    }

    const onUserSessionChanged = async () => {
      authLogger.info('AuthProvider', 'User session changed event')
      const user = await userManager.getUser()

      if (user && !user.expired && user.expires_in > EXPIRY_BUFFER_SECONDS) {
        authLogger.debug('AuthProvider', 'Session valid, no action needed', {
          expires_in: user.expires_in,
        })
        return
      }

      authLogger.warn('AuthProvider', 'Session needs refresh', {
        hasUser: !!user,
        expired: user?.expired,
        expires_in: user?.expires_in,
      })

      if (!silentSigninPromise.current) {
        silentSigninPromise.current = userManager
          .signinSilent()
          .then(user => {
            authLogger.info('AuthProvider', 'Silent signin succeeded after session change')
            return user
          })
          .catch(err => {
            authLogger.error('AuthProvider', 'Silent signin failed after session change', {
              error: err.message,
            })
            throw err
          })
          .finally(() => {
            silentSigninPromise.current = null
          })
      }
      return silentSigninPromise.current
    }

    // Attach events
    authLogger.debug('AuthProvider', 'Attaching OIDC event handlers')
    userManager.events.addUserLoaded(onUserLoaded)
    userManager.events.addUserUnloaded(onUserUnloaded)
    userManager.events.addAccessTokenExpiring(onAccessTokenExpiring)
    userManager.events.addAccessTokenExpired(onAccessTokenExpired)
    userManager.events.addSilentRenewError(onSilentRenewError)
    userManager.events.addUserSignedOut(onUserSignedOut)
    userManager.events.addUserSessionChanged(onUserSessionChanged)

    // Cleanup
    return () => {
      authLogger.debug('AuthProvider', 'Removing OIDC event handlers')
      userManager.events.removeUserLoaded(onUserLoaded)
      userManager.events.removeUserUnloaded(onUserUnloaded)
      userManager.events.removeAccessTokenExpiring(onAccessTokenExpiring)
      userManager.events.removeAccessTokenExpired(onAccessTokenExpired)
      userManager.events.removeSilentRenewError(onSilentRenewError)
      userManager.events.removeUserSignedOut(onUserSignedOut)
      userManager.events.removeUserSessionChanged(onUserSessionChanged)
    }
  }, [userManager, store])

  return React.Children.only(children)
}

export default AuthenticationProvider
